/**
 * Strip HTML tags from a comment-like value.
 * @param {string} value - HTML or plain text value.
 * @returns {string} Plain text value.
 */
export function stripTrustCommentHtml(value) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(String(value || ''), 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}

/**
 * One-line summary for the trust sidebar (citation comment + status, not the full template goal).
 * @param {{ status: string, comment: string }} comment - Extracted trust comment.
 * @returns {string} Short headline text.
 */
export function getTrustCommentHeadline(comment) {
  const isCited = comment.status === 'cited';
  const body = (comment.comment || '').trim() || (isCited ? 'Cited to project sources' : 'Needs attention');
  return isCited ? `Source: ${body}` : `Action required: ${body}`;
}

/**
 * Heading that names a trust section: the element itself when it is a heading, its first
 * heading, or the nearest heading before its top-level block.
 * @param {Element} element - Trust section element.
 * @param {HTMLElement} body - Document body.
 * @returns {string} Heading text ('' when none).
 */
function trustSectionHeading(element, body) {
  if (/^H[1-6]$/.test(element.tagName)) return (element.textContent || '').trim();
  const inner = element.querySelector('h1, h2, h3, h4, h5, h6');
  if (inner) return (inner.textContent || '').trim();
  let top = element;
  while (top.parentElement && top.parentElement !== body) top = top.parentElement;
  for (let node = top.previousElementSibling; node; node = node.previousElementSibling) {
    if (/^H[1-6]$/.test(node.tagName)) return (node.textContent || '').trim();
  }
  return '';
}

/**
 * Extract trust citation comments from generated document HTML.
 * @param {string} htmlContent - Generated document HTML.
 * @returns {Array<{ id: string, sectionIndex: number, goal: string, goalPlain: string, heading: string, confidence: string, sourceLineIds: string[], comment: string, status: string }>} Citation comments.
 */
export function extractTrustComments(htmlContent) {
  if (!htmlContent || typeof DOMParser === 'undefined') return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');

  return Array.from(doc.querySelectorAll('[data-trust-section="true"]')).map((element, index) => {
    const sourceLineIds = (element.getAttribute('data-source-line-ids') || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    const confidence = element.getAttribute('data-confidence') || '';
    const comment = stripTrustCommentHtml(element.getAttribute('data-citation-comment') || '');
    const goal = element.getAttribute('data-template-goal') || `Section ${index + 1}`;
    const goalPlain = stripTrustCommentHtml(goal) || goal;

    return {
      id: `${goal}-${index}`,
      sectionIndex: index,
      goal,
      goalPlain,
      heading: trustSectionHeading(element, doc.body),
      confidence,
      sourceLineIds,
      comment,
      status: sourceLineIds.length > 0 ? 'cited' : 'action_required',
    };
  });
}

/**
 * Return whether document HTML contains trust citation metadata.
 * @param {string} htmlContent - Generated document HTML.
 * @returns {boolean} True when trust citation sections are present.
 */
export function hasTrustComments(htmlContent) {
  return extractTrustComments(htmlContent).length > 0;
}
